// Measurement system for parcel dimensions and weight: imperial uses in/lb, metric uses cm/kg
export enum MeasurementSystems {
  IMPERIAL = 'imperial',
  METRIC = 'metric',
}

// Units used for parcel dimensions and weight. A regular (not const) enum so it can be shared across packages
export enum MeasurementUnits {
  CM = 'cm',
  INCH = 'in',
  KG = 'kg',
  POUND = 'lb',
}

export enum CustomsCurrency {
  CAD = 'CAD',
  USD = 'USD',
}

/**
 * Canada Post "option-code" delivery-handling codes. Free of charge; valid for
 * Canada Post, Canada Post 3PL, and MultiLeg service AC_CP only. Conflicts mirror
 * `CanadaPostServiceOptions` / `CanadaPostCustomizedServiceOptions.DEL` in
 * integration-services (`canada-post/utils/constants.ts:112-117, 234-278`), which
 * remain the UI source; this shared map is the API-validation source.
 */
export enum CanadaPostDeliveryOptionCode {
  DO_NOT_SAFE_DROP = 'DNS',
  LEAVE_AT_DOOR = 'LAD',
  CARD_FOR_PICKUP = 'HFP',
}

export const CANADA_POST_DELIVERY_OPTION_CODES: readonly string[] = Object.values(CanadaPostDeliveryOptionCode);

export const CANADA_POST_DELIVERY_OPTION_CONFLICTS: Readonly<Record<string, readonly string[]>> = {
  DNS: ['LAD', 'HFP'],
  LAD: ['DNS', 'HFP', 'SR'],
  HFP: ['DNS', 'LAD'],
  SR: ['LAD'],
};

/**
 * Return every mutually-exclusive pair present in `codes` (each pair once,
 * upper-case comparison). Empty array when there are no conflicts.
 */
export function findConflictingDeliveryOptionCodes(codes: readonly string[]): Array<[string, string]> {
  const uniqueCodes = Array.from(new Set(codes.map(code => code.toUpperCase())));
  const pairs: Array<[string, string]> = [];

  for (let i = 0; i < uniqueCodes.length; i++) {
    for (let j = i + 1; j < uniqueCodes.length; j++) {
      const a = uniqueCodes[i];
      const b = uniqueCodes[j];
      if ((CANADA_POST_DELIVERY_OPTION_CONFLICTS[a] || []).includes(b)) {
        pairs.push([a, b]);
      }
    }
  }

  return pairs;
}

export enum LabelSizes {
  SMALL = 0,
  LARGE = 1,
}

export enum Statuses {
  CREATED = 'CR',
  CONFIRMED = 'CF',
  CANCELLED = 'CL',
  UNCONFIRMED = 'UC',
  PENDING_CANCEL = 'PC',
  CANCEL_REJECTED = 'CJ',
  PARTIALLY_DELIVERED = 'PD',
}

export enum TrackingStatusV2 {
  // Initial states
  INFO_RECEIVED = 'INFO_RECEIVED', // Electronic information received
  LABEL_CREATED = 'LABEL_CREATED', // Shipping label generated
  PICKUP_SCHEDULED = 'PICKUP_SCHEDULED', // Pickup scheduled with carrier
  PICKED_UP = 'PICKED_UP', // Package collected from sender

  // Transit states
  IN_TRANSIT = 'IN_TRANSIT', // Moving through network
  ARRIVED_FACILITY = 'ARRIVED_FACILITY', // Arrived at distribution center
  DEPARTED_FACILITY = 'DEPARTED_FACILITY', // Left distribution center
  LOCAL_BRANCH = 'LOCAL_BRANCH', // At local delivery facility
  INTERNATIONAL = 'INTERNATIONAL', // International shipping
  CUSTOMS_HOLD = 'CUSTOMS_HOLD', // Held in customs
  CUSTOMS_CLEARED = 'CUSTOMS_CLEARED', // Cleared customs

  // Delivery states
  DELIVERY_SCHEDULED = 'DELIVERY_SCHEDULED', // Delivery appointment set
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY', // On final delivery route
  DELIVERY_ATTEMPTED = 'DELIVERY_ATTEMPTED', // Delivery attempt made
  AVAILABLE_PICKUP = 'AVAILABLE_PICKUP', // Ready for customer pickup

  // Final states
  DELIVERED = 'DELIVERED', // Successfully delivered
  PARTIAL_DELIVERY = 'PARTIAL_DELIVERY', // Partially delivered
  DELIVERED_ALTERNATE = 'DELIVERED_ALTERNATE', // Delivered to alternate location
  RETURN_TO_SENDER = 'RETURN_TO_SENDER', // Unable to complete delivery - marked for return
  RETURNED = 'RETURNED', // Returned to sender
  REFUSED = 'REFUSED', // Delivery refused by recipient
  CANCELLED = 'CANCELLED', // Shipment cancelled by sender or carrier

  // Claim states
  CLAIM_SUBMITTED = 'CLAIM_SUBMITTED', // Claim has been submitted for review
  CLAIM_INVESTIGATION = 'CLAIM_INVESTIGATION', // Claim i s under investigation
  CLAIM_APPROVED = 'CLAIM_APPROVED', // Claim has been approved
  CLAIM_DENIED = 'CLAIM_DENIED', // Claim has been denied

  // Exception states
  DELAYED = 'DELAYED', // Delayed in transit
  WEATHER_DELAYED = 'WEATHER_DELAYED', // Delayed due to weather
  DAMAGED = 'DAMAGED', // Package damaged
  LOST = 'LOST', // Package lost
  ERROR = 'ERROR', // System/tracking error
  UNKNOWN = 'UNKNOWN', // Status cannot be determined
}
